import { DropdownOption, DropdownOptions, DropdownState } from './types'
import { DropdownDOMManager } from './_dom-manager'
import { DropdownEventHandlers } from './_event-handlers'
import { DropdownStateManager } from './_state-manager'

/**
 * Класс для управления dropdown компонентом
 */
export class Dropdown {
  private static instances: Dropdown[] = []

  private element: HTMLElement
  private options: DropdownOptions
  private state: DropdownState

  // Менеджеры
  private domManager: DropdownDOMManager
  private eventHandlers: DropdownEventHandlers
  private stateManager: DropdownStateManager

  /**
   * Создаёт экземпляр Dropdown
   *
   * @param {DropdownOptions} options - Опции компонента
   */
  constructor(options: DropdownOptions) {
    this.options = {
      multiple: false,
      withSearch: false,
      withDefault: false,
      buttonLike: false,
      ...options
    }

    this.element = options.element

    // Инициализируем состояние
    this.state = {
      selectedValue: this.options.multiple ? [] : '',
      opened: false,
      searchValue: ''
    }

    // Регистрируем экземпляр в статическом массиве
    Dropdown.instances.push(this)

    this.init()
  }

  private init(): void {
    // Инициализируем DOM менеджер
    this.domManager = new DropdownDOMManager(this.element)
    const elements = this.domManager.getElements()

    // Инициализируем менеджер состояния
    this.stateManager = new DropdownStateManager(
      this.element,
      elements,
      this.state,
      this.options
    )

    // Инициализируем обработчики событий
    this.eventHandlers = new DropdownEventHandlers(
      this.element,
      elements,
      this.state,
      this.options,
      {
        onToggle: () => this.toggle(),
        onClose: () => this.close(),
        onUpdateValue: (value) => this.updateValue(value),
        onFilterOptions: () => this.stateManager.filterOptions(),
        onReset: () => this.reset(),
        onSetValue: (value) => this.setValue(value)
      }
    )

    this.applyInitialValue()
    this.eventHandlers.bind()
    document.addEventListener(
      'datepicker:open',
      this.handleDatepickerOpen as EventListener
    )
  }

  /**
   * Закрывает dropdown при открытии datepicker
   * @param {Event} event - Событие открытия datepicker
   */
  private handleDatepickerOpen = (event: Event): void => {
    const customEvent = event as CustomEvent<{ element?: HTMLElement }>
    const datepickerElement = customEvent.detail?.element

    if (!this.state.opened || !datepickerElement) {
      return
    }

    if (!this.element.contains(datepickerElement)) {
      this.close()
    }
  }

  private applyInitialValue(): void {
    // eslint-disable-next-line no-undefined
    if (this.options.value !== undefined) {
      this.state.selectedValue = this.options.value
      this.stateManager.updateUI()
    }
  }

  private toggle(): void {
    if (this.state.opened) {
      this.close()
    } else {
      this.open()
    }
  }

  private open(): void {
    // Закрываем все другие открытые дропдауны
    Dropdown.instances.forEach((instance) => {
      if (instance !== this && instance.isOpened()) {
        instance.close()
      }
    })

    this.state.opened = true
    this.stateManager.setContainerVisible(true)
    this.updateContainerAlignment()

    document.dispatchEvent(
      new CustomEvent('dropdown:open', {
        detail: {
          element: this.element,
          name: this.options.name
        }
      })
    )

    // Добавляем обработчики документа
    this.eventHandlers.addDocumentListeners()

    if (this.options.onOpen) {
      this.options.onOpen()
    }
  }

  private updateContainerAlignment(): void {
    const dropdownContainer = this.element.querySelector<HTMLElement>(
      '[data-dropdown-container]'
    )

    if (!dropdownContainer) {
      return
    }

    requestAnimationFrame(() => {
      const pageContainer =
        this.element.closest<HTMLElement>('.container') ||
        document.querySelector<HTMLElement>('.container')

      if (!pageContainer) {
        dropdownContainer.classList.remove('dropdown__container_align-right')
        return
      }

      const dropdownRect = dropdownContainer.getBoundingClientRect()
      const containerRect = pageContainer.getBoundingClientRect()

      if (dropdownRect.right > containerRect.right) {
        dropdownContainer.classList.add('dropdown__container_align-right')
      } else {
        dropdownContainer.classList.remove('dropdown__container_align-right')
      }
    })
  }

  private close(): void {
    this.state.opened = false
    this.stateManager.setContainerVisible(false)

    // Удаляем обработчики документа
    this.eventHandlers.removeDocumentListeners()

    this.stateManager.clearSearch()

    if (this.options.onClose) {
      this.options.onClose()
    }
  }

  private updateValue(value: string | number): void {
    if (this.options.multiple) {
      const selectedArray = this.state.selectedValue as Array<string | number>

      if (selectedArray.includes(value)) {
        // Удаляем значение
        this.state.selectedValue = selectedArray.filter((v) => v !== value)
      } else {
        // Добавляем значение
        this.state.selectedValue = [...selectedArray, value]
      }
      // Для множественного выбора НЕ закрываем дропдаун
    } else {
      // Для одиночного выбора закрываем дропдаун
      this.state.selectedValue = value
      this.close()
    }

    this.stateManager.updateUI()
    this.stateManager.emitChange()
  }

  // Публичные методы
  /**
   * Возвращает текущее выбранное значение или массив значений
   *
   * @return {any} Выбранное значение
   */
  public getValue(): string | number | Array<string | number> {
    return this.state.selectedValue
  }

  public setValue(value: string | number | Array<string | number>): void {
    this.state.selectedValue = value
    this.stateManager.updateUI()
    // Не вызываем emitChange, чтобы не триггерить обработчики при программной установке значения
  }

  public reset(): void {
    this.state.selectedValue = this.options.multiple ? [] : ''
    this.stateManager.updateUI()
    this.stateManager.emitChange()
  }

  public updateOptions(options: DropdownOption[]): void {
    this.options.options = options
    this.domManager.updateOptionsVisibility(options)
    this.stateManager.updateUI()
  }

  public isOpened(): boolean {
    return this.state.opened
  }

  public destroy(): void {
    // Удаляем экземпляр из статического массива
    const index = Dropdown.instances.indexOf(this)
    if (index > -1) {
      Dropdown.instances.splice(index, 1)
    }

    this.eventHandlers.destroy()
    document.removeEventListener(
      'datepicker:open',
      this.handleDatepickerOpen as EventListener
    )
  }
}
